"use client";

import { Button } from "@pex/ui-web/components/ui/button";
import {
  Pagination,
  PaginationContent,
  PaginationEllipsis,
  PaginationItem,
} from "@pex/ui-web/components/ui/pagination";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@pex/ui-web/components/ui/select";
import {
  ToggleGroup,
  ToggleGroupItem,
} from "@pex/ui-web/components/ui/toggle-group";
import Fuse, { type FuseResult } from "fuse.js";
import { Search } from "lucide-react";
import { useRouter } from "next/navigation";
import { useDeferredValue, useEffect, useMemo, useState } from "react";
import { ResourceCard } from "@/components/resource-card";
import {
  FUSE_OPTIONS,
  MIN_QUERY_LENGTH,
  type Resource,
} from "./resource-search-config";

// ── Constants ─────────────────────────────────────────────────────────────────

const PAGE_SIZE = 12;
const LANGUAGES = ["English", "Afrikaans", "isiZulu"] as const;

// ── Types ─────────────────────────────────────────────────────────────────────

interface ResourceType {
  id: string;
  name: string;
  slug: string;
}

// ── Fuse search ───────────────────────────────────────────────────────────────

function rankResults(results: FuseResult<Resource>[]): Resource[] {
  return results.map((r) => r.item);
}

// ── Pagination helpers ────────────────────────────────────────────────────────

function buildPageRange(
  current: number,
  total: number
): (number | "ellipsis")[] {
  if (total <= 7) {
    return Array.from({ length: total }, (_, i) => i + 1);
  }
  if (current <= 4) {
    return [1, 2, 3, 4, 5, "ellipsis", total];
  }
  if (current >= total - 3) {
    return [1, "ellipsis", total - 4, total - 3, total - 2, total - 1, total];
  }
  return [1, "ellipsis", current - 1, current, current + 1, "ellipsis", total];
}

function buildUrl(type: string, lang: string): string {
  const p = new URLSearchParams();
  if (type) {
    p.set("type", type);
  }
  if (lang) {
    p.set("lang", lang);
  }
  const qs = p.toString();
  return `/resources${qs ? `?${qs}` : ""}`;
}

// ── Component ─────────────────────────────────────────────────────────────────

export function ResourcesSearch({
  items,
  resourceTypes,
  initialType = "",
  initialLang = "",
}: {
  items: Resource[];
  resourceTypes: ResourceType[];
  initialType?: string;
  initialLang?: string;
}) {
  const router = useRouter();

  const [activeType, setActiveType] = useState(initialType);
  const [activeLang, setActiveLang] = useState(initialLang);
  const [q, setQ] = useState("");
  const [page, setPage] = useState(1);

  // Sync state when server re-renders with new URL params
  useEffect(() => {
    setActiveType(initialType);
    setPage(1);
  }, [initialType]);

  useEffect(() => {
    setActiveLang(initialLang);
    setPage(1);
  }, [initialLang]);

  // ── Type options ────────────────────────────────────────────────────────────

  // Only surface a filter pill when at least one unarchived item carries that
  // typeLabel. Keeps the bar from advertising empty categories (e.g. when the
  // only assessment is archived).
  const availableLabels = useMemo(
    () => new Set(items.map((item) => item.typeLabel)),
    [items]
  );

  const typeOptions = useMemo(() => {
    const opts: { slug: string; label: string }[] = [
      { slug: "", label: "All" },
    ];
    for (const rt of resourceTypes) {
      if (rt.slug === "collection") {
        continue;
      }
      if (!availableLabels.has(rt.name)) {
        continue;
      }
      opts.push({ slug: rt.slug, label: rt.name });
    }
    if (availableLabels.has("Collection")) {
      opts.push({ slug: "collections", label: "Collections" });
    }
    return opts;
  }, [resourceTypes, availableLabels]);

  // ── Filtering ───────────────────────────────────────────────────────────────

  const activeTypeLabel = useMemo(() => {
    if (!activeType) {
      return null;
    }
    if (activeType === "collections") {
      return "Collection";
    }
    return resourceTypes.find((rt) => rt.slug === activeType)?.name ?? null;
  }, [activeType, resourceTypes]);

  const typeFiltered = useMemo(() => {
    if (!activeTypeLabel) {
      return items;
    }
    return items.filter((item) => item.typeLabel === activeTypeLabel);
  }, [items, activeTypeLabel]);

  const langFiltered = useMemo(() => {
    if (!activeLang) {
      return typeFiltered;
    }
    return typeFiltered.filter((item) => item.language === activeLang);
  }, [typeFiltered, activeLang]);

  // ── Search ──────────────────────────────────────────────────────────────────

  const fuse = useMemo(
    () => new Fuse(langFiltered, FUSE_OPTIONS),
    [langFiltered]
  );

  const deferredQ = useDeferredValue(q);

  const results = useMemo(() => {
    const trimmed = deferredQ.trim();
    if (trimmed.length < MIN_QUERY_LENGTH) {
      return langFiltered;
    }
    return rankResults(fuse.search(trimmed));
  }, [deferredQ, fuse, langFiltered]);

  // ── Pagination ──────────────────────────────────────────────────────────────

  const totalPages = Math.max(1, Math.ceil(results.length / PAGE_SIZE));
  const isSearching = q.trim().length >= MIN_QUERY_LENGTH;
  const safePage = isSearching ? 1 : Math.min(Math.max(1, page), totalPages);
  const pageItems = results.slice(
    (safePage - 1) * PAGE_SIZE,
    safePage * PAGE_SIZE
  );
  const pageRange = buildPageRange(safePage, totalPages);

  // ── Handlers ────────────────────────────────────────────────────────────────

  function handleTypeChange(value: string) {
    setActiveType(value);
    setPage(1);
    router.push(buildUrl(value, activeLang));
  }

  function handleLangChange(value: string) {
    const lang = value === "all" ? "" : value;
    setActiveLang(lang);
    setPage(1);
    router.push(buildUrl(activeType, lang));
  }

  // ── Render ───────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-8">
      {/* Filter bar */}
      <div className="space-y-3">
        {/* Row 1: Search + Language — stacked on mobile, inline from sm */}
        <div className="flex flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1">
            <Search className="pointer-events-none absolute top-1/2 left-3 size-4 -translate-y-1/2 text-muted-foreground" />
            <input
              aria-label="Search resources"
              className="h-9 w-full rounded-full border border-input bg-input/30 py-1 pr-3 pl-9 text-base outline-none transition-colors placeholder:text-muted-foreground focus-visible:border-ring focus-visible:ring-[3px] focus-visible:ring-ring/50 md:text-sm"
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search resources…"
              type="search"
              value={q}
            />
          </div>
          <Select onValueChange={handleLangChange} value={activeLang || "all"}>
            <SelectTrigger className="w-full sm:w-36 sm:shrink-0">
              <SelectValue placeholder="Language" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All languages</SelectItem>
              {LANGUAGES.map((lang) => (
                <SelectItem key={lang} value={lang}>
                  {lang}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>

        {/* Row 2: Type pills — wrap across rows, full active state for clarity */}
        <ToggleGroup
          className="w-full! flex-wrap gap-2"
          onValueChange={handleTypeChange}
          spacing={2}
          type="single"
          value={activeType}
          variant="outline"
        >
          {typeOptions.map((opt) => (
            <ToggleGroupItem
              className="group grow rounded-full! px-4 text-sm data-[state=on]:border-foreground! data-[state=on]:bg-foreground! data-[state=on]:text-background!"
              key={opt.slug}
              value={opt.slug}
            >
              {opt.label}
            </ToggleGroupItem>
          ))}
        </ToggleGroup>
      </div>

      {/* Grid */}
      {pageItems.length > 0 ? (
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {pageItems.map((resource) => (
            <ResourceCard
              cta={resource.cta}
              description={resource.description ?? undefined}
              href={resource.href}
              img={resource.imgUrl}
              key={resource.id}
              meta={resource.meta}
              title={resource.title}
              type={resource.typeLabel}
            />
          ))}
        </div>
      ) : (
        <div className="py-20 text-center">
          <p className="font-semibold text-base">No results found.</p>
          <p className="mt-2 text-muted-foreground text-sm">
            Try different keywords or clear your filters.
          </p>
        </div>
      )}

      {/* Pagination */}
      {totalPages > 1 && (
        <>
          {/* Mobile: prev / page X of Y / next */}
          <div className="flex items-center justify-between sm:hidden">
            <Button
              disabled={safePage === 1}
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              size="default"
              variant="ghost"
            >
              ← Previous
            </Button>
            <span className="text-muted-foreground text-sm">
              {safePage} / {totalPages}
            </span>
            <Button
              disabled={safePage === totalPages}
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              size="default"
              variant="ghost"
            >
              Next →
            </Button>
          </div>

          {/* Desktop: full numbered pagination */}
          <div className="hidden sm:block">
            <Pagination>
              <PaginationContent>
                <PaginationItem>
                  <Button
                    disabled={safePage === 1}
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    size="default"
                    variant="ghost"
                  >
                    ← Previous
                  </Button>
                </PaginationItem>

                {pageRange.map((item, i) =>
                  item === "ellipsis" ? (
                    // biome-ignore lint/suspicious/noArrayIndexKey: stable ellipsis positions
                    <PaginationItem key={`e-${i}`}>
                      <PaginationEllipsis />
                    </PaginationItem>
                  ) : (
                    <PaginationItem key={item}>
                      <Button
                        className="size-9"
                        onClick={() => setPage(item)}
                        size="icon"
                        variant={safePage === item ? "outline" : "ghost"}
                      >
                        {item}
                      </Button>
                    </PaginationItem>
                  )
                )}

                <PaginationItem>
                  <Button
                    disabled={safePage === totalPages}
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    size="default"
                    variant="ghost"
                  >
                    Next →
                  </Button>
                </PaginationItem>
              </PaginationContent>
            </Pagination>
          </div>
        </>
      )}
    </div>
  );
}
